import { Check, Error as ErrorIcon, MyLocation } from "@mui/icons-material";
import { CircularProgress, IconButton } from "@mui/material";
import { type Coordinates } from "@rescuetablet/missions-client";
import { useCallback, useState } from "react";
import { apiEndpoint } from "./config";

type GeolocateState = "initial" | "loading" | "success" | "error";

export default function GeolocateButton({
  address,
  setAddress,
  setCoordinates,
  setError,
}: {
  address: string;
  setAddress: (value: string) => void;
  setCoordinates: (value: Coordinates) => void;
  setError: (message: string) => void;
}) {
  const [state, setState] = useState<GeolocateState>("initial");

  const geolocate = useCallback(async () => {
    if (!address) return;
    setState("loading");

    try {
      const { formatted, location } = await geocodeAddress({ address });
      setAddress(formatted.replace(", Deutschland", ""));
      setCoordinates(location);
      setState("success");
    } catch (error: any) {
      console.error("Error:", error);
      setError(error.message);
      setState("error");
    }
  }, [address, setAddress, setCoordinates, setError]);

  return (
    <IconButton disabled={state === "loading" || !address} onClick={geolocate}>
      <GeolocateStateIcon state={state} />
    </IconButton>
  );
}

function GeolocateStateIcon({ state }: { state: GeolocateState }) {
  switch (state) {
    case "loading":
      return <CircularProgress size="1em" color="inherit" />;
    case "success":
      return <Check color="success" />;
    case "error":
      return <ErrorIcon color="error" />;
    default:
      return <MyLocation />;
  }
}
async function geocodeAddress({
  address,
}: {
  address: string;
}): Promise<{ formatted: string; location: Coordinates }> {
  const search = new URLSearchParams();
  search.set("address", address);

  const response = await fetch(`${apiEndpoint}/geocode?${search.toString()}`);
  if (!response.ok)
    throw new Error(`Fehler: ${response.status} ${response.statusText}`);
  const geocode = await response.json();
  if (geocode.status !== "OK")
    throw new Error("Adresse konnte nicht aufgelöst werden.");
  const {
    formatted_address,
    geometry: {
      location: { lat, lng },
    },
  } = geocode.results[0];

  return {
    formatted: formatted_address,
    location: { latitude: lat, longitude: lng },
  };
}
